import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, RefreshCwIcon, CheckIcon } from 'lucide-react';
export function PhaseCards({
  phase,
  team,
  onComplete
}) {
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [cards, setCards] = useState([]);
  const [currentCard, setCurrentCard] = useState(0);
  const [responses, setResponses] = useState({});
  const [feedback, setFeedback] = useState({});
  // Generate cards based on the phase
  useEffect(() => {
    let phaseCards = [];
    if (phase.id === 2) {
      // Idea Generation
      phaseCards = [{
        id: 1,
        question: 'What problem does your startup solve?',
        instructions: "Describe a significant problem you've observed that needs solving."
      }, {
        id: 2,
        question: 'Who is your target audience?',
        instructions: 'Define the specific group of people who will benefit most from your solution.'
      }, {
        id: 3,
        question: 'What makes your idea unique?',
        instructions: 'Explain what differentiates your approach from existing solutions.'
      }, {
        id: 4,
        question: 'How scalable is your idea?',
        instructions: 'Discuss how your solution could grow and reach more people over time.'
      }, {
        id: 5,
        question: 'What inspired this idea?',
        instructions: 'Share the personal experiences or observations that led to this concept.'
      }];
    } else if (phase.id === 5) {
      // Business Model
      phaseCards = [{
        id: 1,
        question: 'How will your startup generate revenue?',
        instructions: 'Describe your primary revenue streams and pricing strategy.'
      }, {
        id: 2,
        question: "What are your startup's key costs?",
        instructions: 'Identify the major expenses required to launch and operate.'
      }, {
        id: 3,
        question: 'How will you acquire customers?',
        instructions: 'Outline your customer acquisition strategy and channels.'
      }, {
        id: 4,
        question: 'What partnerships will be crucial?',
        instructions: 'Identify key partnerships that will help your startup succeed.'
      }, {
        id: 5,
        question: 'What metrics will define success?',
        instructions: "Describe how you'll measure progress and success."
      }];
    }
    // Shuffle the cards
    const shuffled = [...phaseCards].sort(() => 0.5 - Math.random());
    setCards(shuffled);
  }, [phase.id]);
  const handleResponseChange = e => {
    setResponses({
      ...responses,
      [team[currentPlayer].id]: {
        ...responses[team[currentPlayer].id],
        [cards[currentCard].id]: e.target.value
      }
    });
  };
  const handleFeedbackChange = (playerId, cardId, e) => {
    setFeedback({
      ...feedback,
      [playerId]: {
        ...feedback[playerId],
        [cardId]: e.target.value
      }
    });
  };
  const nextCard = () => {
    if (currentCard < cards.length - 1) {
      setCurrentCard(currentCard + 1);
    } else {
      // Move to next player or complete if all players have gone
      if (currentPlayer < team.length - 1) {
        setCurrentPlayer(currentPlayer + 1);
        setCurrentCard(0);
      } else {
        completePhase();
      }
    }
  };
  const prevCard = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
    } else if (currentPlayer > 0) {
      setCurrentPlayer(currentPlayer - 1);
      setCurrentCard(cards.length - 1);
    }
  };
  const completePhase = () => {
    // Generate a summary of all responses and feedback
    let summary = `## ${phase.name} Summary\n\n`;
    team.forEach(player => {
      summary += `### ${player.name}'s Contributions\n\n`;
      if (responses[player.id]) {
        Object.keys(responses[player.id]).forEach(cardId => {
          const card = cards.find(c => c.id.toString() === cardId);
          if (card) {
            summary += `**${card.question}**\n${responses[player.id][cardId]}\n\n`;
            // Add feedback from other team members
            summary += 'Team Feedback:\n';
            let hasFeedback = false;
            team.forEach(feedbackProvider => {
              if (feedbackProvider.id !== player.id && feedback[feedbackProvider.id] && feedback[feedbackProvider.id][cardId]) {
                summary += `- ${feedbackProvider.name}: ${feedback[feedbackProvider.id][cardId]}\n`;
                hasFeedback = true;
              }
            });
            if (!hasFeedback) {
              summary += '- No feedback provided\n';
            }
            summary += '\n';
          }
        });
      } else {
        summary += 'No responses provided.\n\n';
      }
    });
    onComplete(summary);
  };
  // If no cards are loaded yet, show loading
  if (cards.length === 0) {
    return <div className="flex justify-center items-center p-12" data-id="element-139">
        <RefreshCwIcon className="w-8 h-8 text-indigo-600 animate-spin" data-id="element-140" />
      </div>;
  }
  const currentPlayerData = team[currentPlayer];
  const currentCardData = cards[currentCard];
  const hasResponse = responses[currentPlayerData.id]?.[currentCardData.id]?.length > 0;
  return <div data-id="element-141">
      <div className="mb-6" data-id="element-142">
        <div className="flex justify-between items-center mb-4" data-id="element-143">
          <h3 className="text-lg font-medium" data-id="element-144">
            {currentPlayerData.name}'s Turn ({currentPlayer + 1}/{team.length})
          </h3>
          <div className="text-sm text-gray-500" data-id="element-145">
            Card {currentCard + 1} of {cards.length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white shadow-lg" data-id="element-146">
          <h3 className="text-xl font-bold mb-3" data-id="element-147">{currentCardData.question}</h3>
          <p className="text-indigo-100 mb-4" data-id="element-148">{currentCardData.instructions}</p>
          <div className="mt-4" data-id="element-149">
            <textarea className="w-full p-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-300 focus:outline-none" rows={4} placeholder="Type your answer here..." value={responses[currentPlayerData.id]?.[currentCardData.id] || ''} onChange={handleResponseChange} data-id="element-150"></textarea>
          </div>
        </div>
      </div>
      {/* Previous responses and feedback section */}
      {Object.keys(responses).length > 0 && <div className="mb-6" data-id="element-151">
          <h3 className="text-lg font-medium mb-3" data-id="element-152">
            Team Responses & Feedback
          </h3>
          <div className="space-y-4" data-id="element-153">
            {team.map((player, playerIndex) => {
          // Skip current player if they haven't responded to any cards yet
          if (!responses[player.id] || Object.keys(responses[player.id]).length === 0) {
            return null;
          }
          return <div key={player.id} className="bg-gray-50 rounded-lg p-4" data-id="element-154">
                  <h4 className="font-medium text-indigo-700 mb-2" data-id="element-155">
                    {player.name}'s Responses
                  </h4>
                  {cards.map(card => {
              // Only show cards that this player has responded to
              if (!responses[player.id]?.[card.id]) {
                return null;
              }
              return <div key={card.id} className="mb-4 last:mb-0" data-id="element-156">
                        <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-157">
                          {card.question}
                        </p>
                        <p className="text-sm text-gray-600 bg-white p-2 rounded border border-gray-200 mb-2" data-id="element-158">
                          {responses[player.id][card.id]}
                        </p>
                        {/* Don't show feedback form for the current player */}
                        {player.id !== currentPlayerData.id && <div className="mt-2" data-id="element-159">
                            <label className="text-xs text-gray-500 block mb-1" data-id="element-160">
                              Your feedback:
                            </label>
                            <div className="flex" data-id="element-161">
                              <input type="text" className="flex-1 text-sm p-2 rounded-l border border-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="Provide constructive feedback..." value={feedback[currentPlayerData.id]?.[card.id] || ''} onChange={e => handleFeedbackChange(currentPlayerData.id, card.id, e)} data-id="element-162" />
                              <button className="bg-indigo-100 text-indigo-700 px-3 rounded-r border border-l-0 border-gray-300 hover:bg-indigo-200" onClick={() => {
                      // Mark feedback as submitted
                      const updatedFeedback = {
                        ...feedback
                      };
                      if (!updatedFeedback[currentPlayerData.id]) {
                        updatedFeedback[currentPlayerData.id] = {};
                      }
                      if (!updatedFeedback[currentPlayerData.id][card.id]) {
                        updatedFeedback[currentPlayerData.id][card.id] = 'Great insight!';
                      }
                      setFeedback(updatedFeedback);
                    }} data-id="element-163">
                                <CheckIcon className="w-4 h-4" data-id="element-164" />
                              </button>
                            </div>
                          </div>}
                      </div>;
            })}
                </div>;
        })}
          </div>
        </div>}
      <div className="flex justify-between mt-6" data-id="element-165">
        <button className="px-4 py-2 flex items-center text-indigo-600 hover:text-indigo-800 disabled:text-gray-400" onClick={prevCard} disabled={currentPlayer === 0 && currentCard === 0} data-id="element-166">
          <ChevronLeftIcon className="w-5 h-5 mr-1" data-id="element-167" />
          Previous
        </button>
        <button className={`px-4 py-2 rounded-md flex items-center ${hasResponse ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} onClick={nextCard} disabled={!hasResponse} data-id="element-168">
          {currentPlayer === team.length - 1 && currentCard === cards.length - 1 ? 'Complete Phase' : 'Next'}
          <ChevronRightIcon className="w-5 h-5 ml-1" data-id="element-169" />
        </button>
      </div>
    </div>;
}